import { AxiosError } from 'axios';

import { FormStateValues } from '@components/form';
import { SelectOption } from '@components/select';

import { unhandledRequest } from 'api/request';

export type Cosigner = {
  id: number;
  name: string;
  email: string;
  ssn: string;
};

export type Template = {
  id: number;
  title: string;
};

export type Signer = {
  status: 'pending' | 'signed' | 'declined';
  name: string;
};

export type Contract = {
  id: string;
  data: Record<string, any>;
  templateTitle: string;
  senderName: string;
  senderId: number;
  dateSent: string;
  signers: Signer[];
  url: string;
};

type FormResponse = {
  name: string;
  description: string;
  title: string;
  isSsnValid: boolean;
  cosigners: Cosigner[];
  templates: Template[];
  contracts: Contract[];
  defaultValues: { [key: string]: string };
  departments: SelectOption[];
  job: {
    id: number;
    department: string;
    location: string;
  };
};

type PreviewResponse = {
  url: string;
};

type SubmitResponse = SubmitSuccessResponse | SubmitErrorResponse;

export type SubmitSuccessResponse = {
  ok: boolean;
};

type SubmitErrorResponse = {
  ok: boolean;
  errors: string[];
};

export type SubmitError = AxiosError<SubmitErrorResponse>;

export type SubmitDocumentForm = Record<string, any> & { cosigners: SelectOption[] };

export async function reykjavikGetToken() {
  const response = await unhandledRequest.get<{ ok: boolean; valid: boolean }>({
    url: `/integrations/reykjavik/settings/token/`,
  });
  return response.data;
}

export async function reykjavikForm(applicationID: string) {
  const response = await unhandledRequest.get<FormResponse>({
    url: `/integrations/reykjavik/${applicationID}/form/`,
  });
  return response.data;
}

export async function reykjavikPreviewDocument(
  applicationID: string,
  templateID: string,
  data: FormStateValues
) {
  const response = await unhandledRequest.post<PreviewResponse>({
    url: `/integrations/reykjavik/${applicationID}/form/${templateID}/preview/`,
    data,
  });
  return response.data;
}

export async function reykjavikSubmitDocument(
  applicationID: string,
  templateID: string,
  data: FormStateValues
) {
  const response = await unhandledRequest.post<SubmitResponse>({
    url: `/integrations/reykjavik/${applicationID}/form/${templateID}/sign/`,
    data,
  });
  return response.data;
}

export async function reykjavikExternalLists(list: string, id?: string) {
  const response = await unhandledRequest.get<SelectOption[]>({
    url: `/integrations/reykjavik/external/${list}/`,
    params: {
      id,
    },
  });
  return response.data;
}

export type ExternalListBank = {
  address: string;
  postalCode: string;
  countryCode: string;
  code: string;
  name: string;
  branch: string;
};

export async function reykjavikExternalListBanks() {
  const response = await unhandledRequest.get<ExternalListBank[]>({
    url: `/integrations/reykjavik/external/banks/`,
  });
  return response.data;
}

export type ExternalListPostCode = {
  postalCode: string;
  town: string;
};

export async function reykjavikExternalListPostCodes() {
  const response = await unhandledRequest.get<ExternalListPostCode[]>({
    url: `/integrations/reykjavik/external/post-codes/`,
  });
  return response.data;
}

export type ExternalListCostLocation = {
  costCenterCode: string;
  costCenterName: string;
  projectName: string;
  orgUnitName: string;
  projectCode: string;
  orgUnitID: number;
};

export async function reykjavikExternalListCostLocations(id: string) {
  const response = await unhandledRequest.get<ExternalListCostLocation[]>({
    url: `/integrations/reykjavik/external/cost-location/`,
    params: {
      id,
    },
  });
  return response.data;
}

export type ExternalListJobTitles = {
  code: string;
  name: string;
  id: number;
};

export async function reykjavikExternalListJobTitles(id: string) {
  const response = await unhandledRequest.get<ExternalListJobTitles[]>({
    url: `/integrations/reykjavik/external/job-titles/`,
    params: {
      id,
    },
  });
  return response.data;
}

export type ExternalListUnions = {
  number: number;
  name: string;
  ssn: string;
  postalCode: string;
  address: string;
};

export async function reykjavikExternalListUnions(id: string) {
  const response = await unhandledRequest.get<ExternalListUnions[]>({
    url: `/integrations/reykjavik/external/unions/`,
    params: {
      id,
    },
  });
  return response.data;
}

export type ExternalListJob = {
  basicWageGroup: string;
  unionCode: string;
  unionAgreement: string;
};

export async function reykjavikExternalListJob(id: string) {
  const response = await unhandledRequest.get<ExternalListJob>({
    url: `/integrations/reykjavik/external/jobs/`,
    params: {
      id,
    },
  });
  return response.data;
}

export type ExternalListWorkplace = {
  orgUnitId: string;
  parentId: string;
  name: string;
  description: string;
  costCenterCode: string;
  costCenterName: string;
};

export async function reykjavikExternalListWorkplaces(id: string) {
  const response = await unhandledRequest.get<ExternalListWorkplace[]>({
    url: `/integrations/reykjavik/external/workplaces/`,
    params: {
      id,
    },
  });
  return response.data;
}

export type ExternalListPension = {
  id: string;
  name: string;
};

export async function reykjavikExternalListPensions(id: string) {
  const response = await unhandledRequest.get<ExternalListPension[]>({
    url: `/integrations/reykjavik/external/pensions/`,
    params: {
      id,
    },
  });
  return response.data;
}

export async function reykjavikDeleteDocument(contractId: string) {
  const response = await unhandledRequest.post({
    url: `/integrations/reykjavik/${contractId}/delete/`,
  });
  return response.data;
}

export type JobCaseType = {
  exists: boolean;
  completed?: boolean;
  createdBy?: string;
  dateCreated?: Date;
  totalApplications?: number;
  completedApplications?: number;
  incompleteApplications?: number;
};

export async function getJobCase(jobId: string) {
  const response = await unhandledRequest.get<JobCaseType>({
    url: `/integrations/reykjavik/job-case/${jobId}/`,
  });
  return response.data;
}

export const createJobCase = async (jobId: string) => {
  const response = await unhandledRequest.post({
    url: `/integrations/reykjavik/job-case/${jobId}/`,
  });
  return response.data;
};

export type SAPEmployeeType = {
  name: string;
  description: string;
  title: string;
  contractSigned: boolean;
  exists: boolean;
  sapId?: string;
  createdBy?: string;
  dateCreated?: string;
};

export async function getSapApplication(applicationId: string) {
  const response = await unhandledRequest.get<SAPEmployeeType>({
    url: `/integrations/reykjavik/${applicationId}/sap/`,
  });
  return response.data;
}

export async function createSapApplication(applicationId: string) {
  const response = await unhandledRequest.post({
    url: `/integrations/reykjavik/${applicationId}/sap/`,
  });
  return response.data;
}
